import React from "react";

/**
 * The pricing distribution on page 04, drawn as inline SVG.
 *
 * Coordinates are relative to the artwork box in the template (x 142..914, y 90..265 on
 * the page), so the viewBox is 772 x 175 and everything below is measured inside it.
 * The previous deck positioned the three prices over a background PNG with fixed pixel
 * offsets, which broke whenever the figures changed width.
 */

const W = 772;
const H = 175;
const BASELINE = 168;
const AMPLITUDE = 150;
const MU = 386;
const SIGMA = 130;

// Markers sit on the curve; their labels sit beneath, centred on labelX.
const MARKERS = [
    {key: 'conservative', label: 'CONSERVATIVE', x: 288, labelX: 158},
    {key: 'anticipated', label: 'ANTICIPATED', x: 385, labelX: 385},
    {key: 'aggressive', label: 'AGGRESSIVE', x: 484, labelX: 604},
];

const OUTER_BAND = [184, 585];
const INNER_BAND = [288, 484];

const CONNECTOR_Y = 174;

function curveY(x) {
    return BASELINE - AMPLITUDE * Math.exp(-Math.pow(x - MU, 2) / (2 * Math.pow(SIGMA, 2)));
}

function curvePoints(from, to, step = 4) {
    const points = [];
    for (let x = from; x < to; x += step) points.push([x, curveY(x)]);
    points.push([to, curveY(to)]);
    return points;
}

function toPath(points, prefix = 'M') {
    return points.map(([x, y], i) => `${i === 0 ? prefix : 'L'} ${round(x)} ${round(y)}`).join(' ');
}

function bandPath([from, to]) {
    return `M ${from} ${BASELINE} ${toPath(curvePoints(from, to), 'L')} L ${to} ${BASELINE} Z`;
}

function round(n) {
    return Math.round(n * 100) / 100;
}

/**
 * The office and industrial references print three lines under each band - price, PSF, cap rate -
 * where retail prints two. Verified against the retail multi-tenant and single-tenant reference
 * PDFs, neither of which carries a PSF line, so `showPsf` is off unless a deck asks for it. The
 * offering summary on page 02 splits by product the same way; see OfferingSummary.
 */
export default function BellCurve({conservative, anticipated, aggressive, showPsf}) {
    const values = {conservative, anticipated, aggressive};

    return (
        <div className='bov-curve'>
            <svg
                className='bov-curve__svg'
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio='xMidYMid meet'
                role='presentation'
            >
                <path className='bov-curve__band bov-curve__band--outer' d={bandPath(OUTER_BAND)}/>
                <path className='bov-curve__band bov-curve__band--inner' d={bandPath(INNER_BAND)}/>
                <path className='bov-curve__line' d={toPath(curvePoints(0, W))} fill='none'/>

                {MARKERS.map(marker => (
                    <g key={marker.key}>
                        <path className='bov-curve__connector' d={connectorPath(marker)} fill='none'/>
                        <circle className='bov-curve__dot' cx={marker.x} cy={round(curveY(marker.x))} r='9'/>
                    </g>
                ))}
            </svg>

            <div className='bov-curve__labels'>
                {MARKERS.map(marker => (
                    <div
                        key={marker.key}
                        className='bov-curve__label'
                        style={{left: `${marker.labelX}px`}}
                    >
                        <div className='bov-curve__labelTitle'>{marker.label}</div>
                        <div className='bov-curve__labelValue'>{values[marker.key].price}</div>
                        {showPsf ? (
                            <div className='bov-curve__labelValue'>
                                {values[marker.key].psf || '-'} PSF
                            </div>
                        ) : null}
                        <div className='bov-curve__labelValue'>{values[marker.key].capRate} Cap</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function connectorPath(marker) {
    const yOnCurve = round(curveY(marker.x));
    if (marker.x === marker.labelX) {
        return `M ${marker.x} ${yOnCurve} L ${marker.x} ${CONNECTOR_Y}`;
    }
    const kink = BASELINE - 12;
    return [
        `M ${marker.x} ${yOnCurve}`,
        `L ${marker.x} ${kink}`,
        `L ${marker.labelX} ${kink}`,
        `L ${marker.labelX} ${CONNECTOR_Y}`,
    ].join(' ');
}
